/**
 * Feedback & ratings contract (#200, FR-16). The payload shape + pure validators for the
 * post-game rating card and the generic "Share feedback" surface, living in
 * `@jeopardy/shared` so the web client (inline pre-submit check) and the server (the
 * authority) validate against ONE definition and can never drift — the same posture as
 * {@link ./legal} and {@link ./validator}.
 *
 * This is a **control-plane concern, NOT the game WS protocol** (D200.2): feedback is
 * submitted over `POST /api/feedback` (HTTP), never a WS command/event, so it does NOT
 * add a wire message and does NOT affect {@link PROTOCOL_VERSION}. Feedback text + the
 * optional contact email are control-plane/admin-only and MUST NOT reach the game wire or
 * any other player's view (D200.5, privacy — cf. the email/birth-year privacy rules).
 */

/**
 * What a piece of feedback is about. `rating` is the post-game thumbs card; the rest are
 * the generic-surface category chips (idea / bug / praise / other — DI-B).
 */
export type FeedbackType = 'rating' | 'idea' | 'bug' | 'praise' | 'other';

/** All valid {@link FeedbackType}s (source of truth for validation + the UI chips). */
export const FEEDBACK_TYPES: readonly FeedbackType[] = [
  'rating',
  'idea',
  'bug',
  'praise',
  'other',
] as const;

/** The post-game rating (DI-A: thumbs up/down only — no stars/NPS). */
export type FeedbackRating = 'up' | 'down';

/** Where the feedback was submitted from — recorded for owner triage. */
export type FeedbackSurface = 'post-game' | 'generic';

/** All valid {@link FeedbackSurface}s. */
export const FEEDBACK_SURFACES: readonly FeedbackSurface[] = [
  'post-game',
  'generic',
] as const;

/** Max length of the free-text comment, after trimming (bounds the stored item). */
export const MAX_FEEDBACK_TEXT_LEN = 2000;
/** Max length of the optional contact email (RFC 5321 local+domain ceiling). */
export const MAX_FEEDBACK_EMAIL_LEN = 254;
/** Max length of an associated `gameId` (defensive — it's a server-shaped id). */
export const MAX_FEEDBACK_GAME_ID_LEN = 200;

/**
 * The client → server submission payload for `POST /api/feedback`. Every field except
 * `type` + `surface` is optional; the server attaches identity (`playerId`) itself from a
 * signed token (never trusted from this payload — D200.3) and stamps `createdAtEpochMs`.
 */
export interface FeedbackSubmission {
  type: FeedbackType;
  surface: FeedbackSurface;
  /** Present for a post-game thumbs rating; absent for a text-only generic note. */
  rating?: FeedbackRating;
  /** The free-text comment (optional on a post-game rating; usually present on generic). */
  text?: string;
  /** Optional contact email so the owner can reply out-of-band (DI-C). */
  contactEmail?: string;
  /** The game this post-game feedback is about (post-game surface only). */
  gameId?: string;
}

/**
 * A normalized, validated submission — trimmed strings, empties dropped. This is what the
 * server persists (after attaching identity + a timestamp). Only the fields that survived
 * validation are present.
 */
export interface NormalizedFeedback {
  type: FeedbackType;
  surface: FeedbackSurface;
  rating?: FeedbackRating;
  text?: string;
  contactEmail?: string;
  gameId?: string;
}

export type FeedbackValidation =
  { ok: true; value: NormalizedFeedback } | { ok: false; error: string };

/** A pragmatic email shape check (mirrors the registration route's regex). */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Validate + normalize a caller-supplied feedback submission (FR-16.1/16.2). Pure and
 * network-free so BOTH the web client (pre-submit UX) and the server (authority) run the
 * exact same rules. Returns the normalized value on success, or a short human-readable
 * `error` string the caller can surface.
 *
 * Rules:
 *   - `type` must be a known {@link FeedbackType}; `surface` a known {@link FeedbackSurface}.
 *   - `rating`, when present, must be `'up'` or `'down'`; a `post-game`/`rating` submission
 *     REQUIRES a rating (the thumbs is the whole point of that card).
 *   - `text` is trimmed and bounded to {@link MAX_FEEDBACK_TEXT_LEN}; an empty/whitespace
 *     comment is dropped (never stored as `""`).
 *   - `contactEmail`, when non-empty, must look like an email and be within
 *     {@link MAX_FEEDBACK_EMAIL_LEN}.
 *   - the submission must carry SOME signal — at least a `rating` or non-empty `text` —
 *     so we never persist an empty row.
 */
export function validateFeedback(input: unknown): FeedbackValidation {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, error: 'feedback must be an object' };
  }
  const raw = input as {
    type?: unknown;
    surface?: unknown;
    rating?: unknown;
    text?: unknown;
    contactEmail?: unknown;
    gameId?: unknown;
  };

  if (
    typeof raw.type !== 'string' ||
    !FEEDBACK_TYPES.includes(raw.type as FeedbackType)
  ) {
    return { ok: false, error: 'unknown feedback type' };
  }
  const type = raw.type as FeedbackType;

  if (
    typeof raw.surface !== 'string' ||
    !FEEDBACK_SURFACES.includes(raw.surface as FeedbackSurface)
  ) {
    return { ok: false, error: 'unknown feedback surface' };
  }
  const surface = raw.surface as FeedbackSurface;

  let rating: FeedbackRating | undefined;
  if (raw.rating !== undefined && raw.rating !== null) {
    if (raw.rating !== 'up' && raw.rating !== 'down') {
      return { ok: false, error: 'rating must be "up" or "down"' };
    }
    rating = raw.rating;
  }
  if ((surface === 'post-game' || type === 'rating') && rating === undefined) {
    return { ok: false, error: 'a post-game rating requires a thumbs up or down' };
  }

  let text: string | undefined;
  if (raw.text !== undefined && raw.text !== null) {
    if (typeof raw.text !== 'string') {
      return { ok: false, error: 'text must be a string' };
    }
    const trimmed = raw.text.trim();
    if (trimmed.length > MAX_FEEDBACK_TEXT_LEN) {
      return { ok: false, error: 'text is too long' };
    }
    if (trimmed.length > 0) text = trimmed;
  }

  let contactEmail: string | undefined;
  if (raw.contactEmail !== undefined && raw.contactEmail !== null) {
    if (typeof raw.contactEmail !== 'string') {
      return { ok: false, error: 'contactEmail must be a string' };
    }
    const trimmed = raw.contactEmail.trim();
    if (trimmed.length > 0) {
      if (trimmed.length > MAX_FEEDBACK_EMAIL_LEN || !looksLikeEmail(trimmed)) {
        return { ok: false, error: 'contactEmail must be a valid email address' };
      }
      contactEmail = trimmed;
    }
  }

  let gameId: string | undefined;
  if (raw.gameId !== undefined && raw.gameId !== null) {
    if (typeof raw.gameId !== 'string') {
      return { ok: false, error: 'gameId must be a string' };
    }
    const trimmed = raw.gameId.trim();
    if (trimmed.length > MAX_FEEDBACK_GAME_ID_LEN) {
      return { ok: false, error: 'gameId is too long' };
    }
    if (trimmed.length > 0) gameId = trimmed;
  }

  if (rating === undefined && text === undefined) {
    return { ok: false, error: 'add a rating or a comment before sending' };
  }

  return {
    ok: true,
    value: {
      type,
      surface,
      ...(rating !== undefined ? { rating } : {}),
      ...(text !== undefined ? { text } : {}),
      ...(contactEmail !== undefined ? { contactEmail } : {}),
      ...(gameId !== undefined ? { gameId } : {}),
    },
  };
}
